import React, { useState, useEffect, useMemo, useRef } from "react";
import { fetchAllSupervisors } from './FeedbackPaginationUtils';
import PersonIcon from '@mui/icons-material/Person';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import { useNavigate, useLocation } from 'react-router-dom';
import DepartureTopStrip from '../components/ui/DepartureTopStrip';
import PhoneTopStrip from '../components/ui/PhoneTopStrip';
import useRenderMode from '../hooks/useRenderMode';
import { useZones, getSupervisorPrimaryZoneName } from '../hooks/useZones';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { collection, query, where, getCountFromServer, getDocs, getFirestore, Timestamp, orderBy, limit } from 'firebase/firestore';
import { MapContainer, Marker, Polyline, useMap } from 'react-leaflet';
import { MapboxTileLayer, makeCircleIcon } from '../lib/leafletInit';
import { resolveSupervisorIxoraIds, whereFeedbackByAnySupervisorId } from '../utils/supervisorIds';
import { fetchSupervisorLocationHistory, locationSamplesToMapPoints } from '../lib/supervisorLocationApi';

// ---------- map helpers ----------

const INDIA_CENTER = [20.5937, 78.9629];

/** Fit map to an array of {lat,lng} points. */
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) { map.setView([points[0].lat, points[0].lng], 16); return; }
    map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [30, 30] });
  }, [map, points]);
  return null;
};

const pad2 = (n) => String(n).padStart(2, '0');
const toLocalDateInputValue = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const parseDateInputValue = (value) => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const haversineMeters = (a, b) => {
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
};

const totalDistanceKm = (points) => {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += haversineMeters(points[i - 1], points[i]);
  }
  return total / 1000;
};

const formatDuration = (ms) => {
  if (!ms || ms < 0) return '0m';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};


const SupervisorInformation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mode = useRenderMode();
  const { zoneById, zoneByAreaNumber } = useZones();
  const supervisorZoneName = (s) => getSupervisorPrimaryZoneName(s, zoneById, zoneByAreaNumber);
  const [supervisors, setSupervisors] = useState([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [supervisorSearchQuery, setSupervisorSearchQuery] = useState("");
  const [isSupervisorDropdownOpen, setIsSupervisorDropdownOpen] = useState(false);
  const supervisorDropdownRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [feedbackData, setFeedbackData] = useState([]);
  const [checklistData, setChecklistData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const monthPickerRef = useRef(null);

  // Location tracking state
  const [mapDate, setMapDate] = useState(() => toLocalDateInputValue(new Date()));
  const [locationHistory, setLocationHistory] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showFeedbackPins, setShowFeedbackPins] = useState(false);
  const [feedbackPins, setFeedbackPins] = useState([]);

  // Local YYYY-MM-DD formatter (no timezone shift)
  const formatDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const monthGrid = [
    ["Jan", "Feb", "Mar", "Apr"],
    ["May", "Jun", "Jul", "Aug"],
    ["Sep", "Oct", "Nov", "Dec"]
  ];

  // Close month picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(event.target)) {
        setShowMonthPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchFeedbackCount = async (supervisor) => {
    const idClause = whereFeedbackByAnySupervisorId(supervisor);
    if (!idClause) { setFeedbackCount(0); return; }
    try {
      const db = getFirestore();
      const feedbackRef = collection(db, 'feedback_collection');
      // Union over current + legacyIxoraUserIds so post-migration supervisors
      // still see their historical feedback. See utils/supervisorIds.js.
      const supervisorQuery = query(feedbackRef, idClause);
      // Subtract silent_duplicate fraud-trap docs from the supervisor's count
      // so it never inflates payroll/KPIs.  Two server-side aggregations is
      // still O(1) Firestore reads.
      const [totalSnap, silentSnap] = await Promise.all([
        getCountFromServer(supervisorQuery),
        getCountFromServer(query(supervisorQuery, where('silent_duplicate', '==', true))),
      ]);
      setFeedbackCount(Math.max(0, totalSnap.data().count - silentSnap.data().count));
    } catch (error) {
      console.error("Error fetching feedback count:", error);
      setFeedbackCount(0);
    }
  };

  const fetchFeedbackDataByDate = async (supervisor, month, year) => {
    const idClause = whereFeedbackByAnySupervisorId(supervisor);
    if (!idClause) { setFeedbackData([]); return; }
    try {
      const db = getFirestore();
      const feedbackRef = collection(db, 'feedback_collection');
      // Scope the query to the selected month so we don't load every feedback
      // the supervisor has ever submitted (can be thousands of docs).
      const start = new Date(year, month, 1);
      const end   = new Date(year, month + 1, 1);
      const q = query(
        feedbackRef,
        idClause,
        where('feedback_collection_DateTime', '>=', Timestamp.fromDate(start)),
        where('feedback_collection_DateTime', '<',  Timestamp.fromDate(end)),
      );
      const querySnapshot = await getDocs(q);
      const feedbackByDate = {};
      const daysInMonth = getDaysInMonth(year, month);
      for (let day = 1; day <= daysInMonth; day++) {
        feedbackByDate[`${day}/${month + 1}`] = 0;
      }
      querySnapshot.forEach(doc => {
        const data = doc.data();
        // Exclude silent_duplicate fraud-trap rows from supervisor's chart.
        if (data.silent_duplicate === true) return;
        let feedbackDate;
        if (data.feedback_collection_DateTime) {
          if (data.feedback_collection_DateTime.toDate) feedbackDate = data.feedback_collection_DateTime.toDate();
          else if (typeof data.feedback_collection_DateTime === 'string') feedbackDate = new Date(data.feedback_collection_DateTime);
        } else if (data.feedback_collection_Date) {
          if (data.feedback_collection_Date.toDate) feedbackDate = data.feedback_collection_Date.toDate();
          else if (typeof data.feedback_collection_Date === 'string') {
            const [yyyy, mm, dd] = data.feedback_collection_Date.split('-').map(Number);
            feedbackDate = new Date(yyyy, mm - 1, dd);
          }
        }
        if (feedbackDate && feedbackDate.getMonth() === month && feedbackDate.getFullYear() === year) {
          const day = feedbackDate.getDate();
          const dateStr = `${day}/${month + 1}`;
          feedbackByDate[dateStr] = (feedbackByDate[dateStr] || 0) + 1;
        }
      });
      const chartData = Object.keys(feedbackByDate).map(date => ({ name: date, feedbacks: feedbackByDate[date] }));
      chartData.sort((a, b) => parseInt(a.name.split('/')[0]) - parseInt(b.name.split('/')[0]));
      setFeedbackData(chartData);
    } catch (error) {
      console.error("Error fetching feedback data by date:", error);
      setFeedbackData([]);
    }
  };

  const fetchChecklistData = async (supervisor, month, year) => {
    if (!supervisor || !supervisor.id) { setChecklistData([]); return; }
    try {
      const db = getFirestore();
      const checklistRef = collection(db, 'supervisors', supervisor.id, 'checklist');
      const querySnapshot = await getDocs(checklistRef);
      const daysInMonth = getDaysInMonth(year, month);
      const checklistMap = {};
      for (let day = 1; day <= daysInMonth; day++) {
        const dateObj = new Date(year, month, day);
        checklistMap[formatDateString(dateObj)] = { status: 'not-done' };
      }
      querySnapshot.forEach(doc => {
        const data = doc.data();
        const dateStrRaw = data && data.checklistDate;
        if (!dateStrRaw || typeof dateStrRaw !== 'string') return;
        const dateStr = dateStrRaw.trim();
        const [yStr, mStr, dStr] = dateStr.split('-');
        const y = parseInt(yStr, 10);
        const m = parseInt(mStr, 10);
        const d = parseInt(dStr, 10);
        if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return;
        if (y === year && (m - 1) === month) {
          const normalized = `${yStr}-${mStr.padStart(2, '0')}-${dStr.padStart(2, '0')}`;
          checklistMap[normalized] = { status: data.isWeekOff ? 'week-off' : 'done' };
        }
      });
      const calendarData = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const dateObj = new Date(year, month, day);
        const formattedDate = formatDateString(dateObj);
        calendarData.push({ day, status: (checklistMap[formattedDate] || { status: 'not-done' }).status, date: dateObj, key: formattedDate });
      }
      setChecklistData(calendarData);
    } catch (error) {
      console.error("Error fetching checklist data:", error);
      setChecklistData([]);
    }
  };

  useEffect(() => {
    try { window.scrollTo({ top: 0, behavior: 'instant' }); } catch (_) { window.scrollTo(0, 0); }
    const loadSupervisors = async () => {
      setLoading(true);
      try {
        const supervisorsList = await fetchAllSupervisors();
        supervisorsList.sort((a, b) => {
          if (!a.ixora_user_id) return 1;
          if (!b.ixora_user_id) return -1;
          return a.ixora_user_id.localeCompare(b.ixora_user_id, undefined, { numeric: true });
        });
        setSupervisors(supervisorsList);
        const fromStateId = location.state && location.state.supervisorId;
        if (fromStateId) {
          const match = supervisorsList.find(s => s.id === fromStateId);
          setSelectedSupervisor(match || (supervisorsList.length > 0 ? supervisorsList[0] : null));
        } else if (supervisorsList.length > 0) {
          setSelectedSupervisor(supervisorsList[0]);
        }
      } catch (error) {
        console.error("Error loading supervisors:", error);
      } finally {
        setLoading(false);
      }
    };
    loadSupervisors();
  }, []);

  const filteredSupervisors = useMemo(() => {
    const q = supervisorSearchQuery.trim().toLowerCase();
    if (!q) return supervisors;
    return supervisors.filter((s) => {
      const name = (s.display_name || "").toLowerCase();
      const id = (s.ixora_user_id || "").toLowerCase();
      const area = (supervisorZoneName(s) || s.ixora_allotment || "").toLowerCase();
      return name.includes(q) || id.includes(q) || area.includes(q);
    });
  }, [supervisors, supervisorSearchQuery]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (supervisorDropdownRef.current && !supervisorDropdownRef.current.contains(e.target)) {
        setIsSupervisorDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => { if (selectedSupervisor) fetchFeedbackCount(selectedSupervisor); }, [selectedSupervisor]);
  useEffect(() => { if (selectedSupervisor) fetchFeedbackDataByDate(selectedSupervisor, selectedMonth, selectedYear); }, [selectedSupervisor, selectedMonth, selectedYear]);
  useEffect(() => { if (selectedSupervisor) fetchChecklistData(selectedSupervisor, selectedMonth, selectedYear); }, [selectedSupervisor, selectedMonth, selectedYear]);

  // Fetch location history when supervisor or mapDate changes (ixora-one via admin API)
  useEffect(() => {
    if (!selectedSupervisor) { setLocationHistory([]); return; }
    let cancelled = false;
    (async () => {
      setLocationLoading(true);
      try {
        const start = parseDateInputValue(mapDate);
        const end = new Date(start);
        end.setDate(end.getDate() + 1);
        const result = await fetchSupervisorLocationHistory({
          feedbackSupervisorUid: selectedSupervisor.id,
          startMs: start.getTime(),
          endMs: end.getTime(),
          limit: 500,
        });
        if (cancelled) return;
        setLocationHistory(result.samples ?? []);
      } catch (err) {
        console.error('Location history fetch error:', err);
        if (!cancelled) setLocationHistory([]);
      } finally {
        if (!cancelled) setLocationLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSupervisor, mapDate]);

  // Fetch feedback pins when toggle is on
  useEffect(() => {
    if (!showFeedbackPins || !selectedSupervisor) { setFeedbackPins([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const db = getFirestore();
        const start = parseDateInputValue(mapDate);
        const end = new Date(start);
        end.setDate(end.getDate() + 1);
        const q = query(
          collection(db, 'feedback_collection'),
          where('feedback_collector_firebase_userID', '==', selectedSupervisor.id),
          where('feedback_collection_DateTime', '>=', Timestamp.fromDate(start)),
          where('feedback_collection_DateTime', '<', Timestamp.fromDate(end)),
          orderBy('feedback_collection_DateTime', 'asc'),
          limit(100),
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const pins = snap.docs
          .map((d) => {
            const data = d.data();
            const geo = data.geoAtSubmit ?? data.feedback_collection_location;
            const lat = geo?.lat ?? geo?.latitude;
            const lng = geo?.lng ?? geo?.longitude;
            if (lat == null || lng == null) return null;
            return { id: d.id, lat, lng, shopName: data.shop_name || '—' };
          })
          .filter(Boolean);
        setFeedbackPins(pins);
      } catch (err) {
        console.error('Feedback pins fetch error:', err);
        if (!cancelled) setFeedbackPins([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSupervisor, mapDate, showFeedbackPins]);

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

  // Map points derived from location history
  const mapPoints = useMemo(
    () => locationSamplesToMapPoints(locationHistory),
    [locationHistory],
  );

  const mapStats = useMemo(() => {
    if (mapPoints.length === 0) return { distanceKm: 0, durationMs: 0, samples: 0, mockedCount: 0 };
    const first = mapPoints[0];
    const last = mapPoints[mapPoints.length - 1];
    return {
      distanceKm: totalDistanceKm(mapPoints),
      durationMs: Math.max(0, last.t - first.t),
      samples: mapPoints.length,
      mockedCount: mapPoints.reduce((acc, p) => acc + (p.isMocked ? 1 : 0), 0),
    };
  }, [mapPoints]);

  const visualizationData = useMemo(() => {
    if (!selectedSupervisor) return { lineData: [], calendarData: [] };
    return { lineData: feedbackData, calendarData: checklistData };
  }, [selectedSupervisor, feedbackData, checklistData]);

  const handleMonthSelect = (monthIndex) => { setSelectedMonth(monthIndex); setShowMonthPicker(false); };
  const toggleMonthPicker = () => setShowMonthPicker(!showMonthPicker);
  const changeYear = (increment) => setSelectedYear(selectedYear + increment);
  const setToCurrentMonth = () => {
    const now = new Date();
    setSelectedMonth(now.getMonth());
    setSelectedYear(now.getFullYear());
    setShowMonthPicker(false);
  };
  // Shared supervisor selector dropdown (used in both phone and desk)
  const renderSupervisorDropdown = (labelStyle, containerStyle) => (
    <div ref={supervisorDropdownRef} style={{ position: 'relative', ...containerStyle }}>
      <button
        onClick={() => setIsSupervisorDropdownOpen(!isSupervisorDropdownOpen)}
        style={{
          width: "100%", padding: "0.6rem 0.75rem",
          border: "1px solid var(--c-line-2)", fontSize: "0.95rem",
          color: "var(--c-ink)", backgroundColor: "var(--c-bg)",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
          backgroundSize: "16px", textAlign: 'left', fontFamily: "var(--font-mono)",
          letterSpacing: '.08em', cursor: 'pointer',
          ...labelStyle
        }}
      >
        {selectedSupervisor
          ? `${selectedSupervisor.display_name || "Unknown"} · ${selectedSupervisor.ixora_user_id || "No ID"}${(supervisorZoneName(selectedSupervisor) || selectedSupervisor.ixora_allotment) ? ` · ${supervisorZoneName(selectedSupervisor) || selectedSupervisor.ixora_allotment}` : ''}${selectedSupervisor.userStatus === 'inactive' ? ' · [INACTIVE]' : ''}`
          : 'SELECT SUPERVISOR'}
      </button>
      {isSupervisorDropdownOpen && (
        <div style={{
          position: 'absolute', top: '110%', left: 0, right: 0,
          background: 'var(--c-bg)', border: '1px solid var(--c-line-2)',
          boxShadow: '0 8px 20px rgba(40,30,20,0.08)', zIndex: 20, overflow: 'hidden'
        }}>
          <div style={{ padding: '8px', borderBottom: '1px solid var(--c-line-soft)' }}>
            <input
              autoFocus type="text" placeholder="Search name, ID, or area"
              value={supervisorSearchQuery} onChange={(e) => setSupervisorSearchQuery(e.target.value)}
              style={{
                width: '100%', padding: '8px 10px',
                border: '1px solid var(--c-line-2)', fontSize: '0.9rem',
                backgroundColor: 'var(--c-bg)', color: 'var(--c-ink)',
                fontFamily: 'var(--font-mono)', boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
            {filteredSupervisors.length === 0 ? (
              <div style={{ padding: '10px 12px', color: 'var(--c-ink-3)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>NO MATCHES</div>
            ) : (
              filteredSupervisors.map((supervisor) => (
                <div
                  key={supervisor.id}
                  onClick={() => { setSelectedSupervisor(supervisor); setIsSupervisorDropdownOpen(false); }}
                  style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--c-line-soft)', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.08em', color: 'var(--c-ink-2)' }}
                  onMouseOver={(e) => (e.currentTarget.style.background = 'var(--c-bg-2)')}
                  onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {(supervisor.display_name || 'Unknown').toUpperCase()} · {(supervisor.ixora_user_id || 'No ID')} {(supervisorZoneName(supervisor) || supervisor.ixora_allotment) ? `· ${supervisorZoneName(supervisor) || supervisor.ixora_allotment}` : ''}{supervisor.userStatus === 'inactive' ? ' · [INACTIVE]' : ''}
                </div>
              ))
            )}
          </div>
          <div style={{ padding: '6px 12px', color: 'var(--c-ink-3)', fontSize: 10, fontFamily: 'var(--font-mono)', letterSpacing: '.10em', borderTop: '1px solid var(--c-line-soft)' }}>
            SHOWING {filteredSupervisors.length} OF {supervisors.length}
          </div>
        </div>
      )}
    </div>
  );

  // Shared month picker popup
  const renderMonthPicker = () => (
    <div style={{ position: 'relative' }}>
      <button
        onClick={toggleMonthPicker}
        className="db-pill"
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <CalendarIcon style={{ fontSize: '14px' }} />
        {months[selectedMonth].toUpperCase().slice(0, 3)} {selectedYear}
      </button>
      {showMonthPicker && (
        <div
          ref={monthPickerRef}
          style={{
            position: 'absolute', top: '110%', right: 0, width: '260px',
            backgroundColor: 'var(--c-ink)', borderRadius: 0,
            boxShadow: '0 4px 20px rgba(40,30,20,0.20)', zIndex: 100,
            padding: '12px', color: 'var(--c-bg)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <button onClick={() => changeYear(-1)} style={{ background: 'none', border: 'none', color: 'var(--c-bg)', cursor: 'pointer', fontSize: 18 }}>‹</button>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '.14em', fontWeight: 600 }}>{selectedYear}</div>
            <button onClick={() => changeYear(1)} style={{ background: 'none', border: 'none', color: 'var(--c-bg)', cursor: 'pointer', fontSize: 18 }}>›</button>
          </div>
          {monthGrid.map((row, rowIndex) => (
            <div key={rowIndex} style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0' }}>
              {row.map((month, colIndex) => {
                const monthIndex = rowIndex * 4 + colIndex;
                const isSelected = monthIndex === selectedMonth;
                return (
                  <button
                    key={colIndex}
                    onClick={() => handleMonthSelect(monthIndex)}
                    style={{
                      width: '56px', padding: '6px 0',
                      background: isSelected ? 'var(--c-brand)' : 'rgba(255,255,255,.1)',
                      border: 'none', color: 'var(--c-bg)', cursor: 'pointer',
                      fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em',
                      fontWeight: isSelected ? 700 : 400
                    }}
                  >
                    {month.toUpperCase()}
                  </button>
                );
              })}
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid rgba(255,255,255,.15)', marginTop: '8px', paddingTop: '8px' }}>
            <button onClick={setToCurrentMonth} style={{ background: 'none', border: 'none', color: 'var(--c-brand)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em' }}>
              THIS MONTH
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Shared calendar grid (hairline-border cells per design spec)
  const renderCalendar = () => {
    const firstDay = new Date(selectedYear, selectedMonth, 1).getDay();
    const today = new Date();
    return (
      <div>
        {/* Day names header */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: 4 }}>
          {['SUN','MON','TUE','WED','THU','FRI','SAT'].map(d => (
            <div key={d} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', color: 'var(--c-ink-3)', padding: '4px 0' }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} style={{ height: 34 }} />
          ))}
          {visualizationData.calendarData.map((dayData) => {
            const isToday = (
              dayData.date.getDate() === today.getDate() &&
              dayData.date.getMonth() === today.getMonth() &&
              dayData.date.getFullYear() === today.getFullYear()
            );
            let borderColor = 'var(--c-line-2)';
            let textColor = 'var(--c-ink-3)';
            if (dayData.status === 'done') { borderColor = 'var(--c-success)'; textColor = 'var(--c-success)'; }
            else if (dayData.status === 'not-done') { borderColor = 'var(--c-danger)'; textColor = 'var(--c-danger)'; }
            return (
              <div
                key={dayData.key}
                title={`${dayData.day} ${months[selectedMonth]}: ${dayData.status === 'done' ? 'Checklist Done' : dayData.status === 'week-off' ? 'Week Off' : 'Not Done'}`}
                style={{
                  height: 34, border: `1px solid ${borderColor}`,
                  boxSizing: 'border-box', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', cursor: 'default',
                  outline: isToday ? '2px solid var(--c-brand)' : 'none',
                  outlineOffset: isToday ? 1 : 0,
                }}
              >
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 11,
                  fontVariantNumeric: 'tabular-nums', fontWeight: isToday ? 700 : 400,
                  color: textColor,
                }}>
                  {dayData.day}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Shared line chart
  const renderLineChart = () => (
    <div style={{ height: 160 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={visualizationData.lineData} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--c-line-soft)" />
          <XAxis
            dataKey="name" tick={{ fontSize: 10, fontFamily: 'var(--font-mono)', fill: 'var(--c-ink-3)' }}
            interval={Math.floor(Math.max(1, visualizationData.lineData.length / 10))}
            tickFormatter={(label) => {
              const [dayStr, _monthStr] = String(label).split('/');
              const day = parseInt(dayStr, 10);
              if (Number.isNaN(day)) return label;
              const suffix = (d) => { if (d > 3 && d < 21) return 'th'; switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; } };
              return `${day}${suffix(day)}`;
            }}
          />
          <YAxis tick={{ fontSize: 10, fontFamily: 'var(--font-mono)', fill: 'var(--c-ink-3)' }} />
          <Tooltip
            formatter={(value) => [value, 'Feedbacks']}
            labelFormatter={(label) => {
              const [day, month] = label.split('/');
              const suffix = (d) => { if (d > 3 && d < 21) return 'th'; switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; } };
              const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              return `${parseInt(day)}${suffix(parseInt(day))} ${monthNames[parseInt(month) - 1]}`;
            }}
            contentStyle={{ fontFamily: 'var(--font-mono)', fontSize: 11, border: '1px solid var(--c-line-2)', background: 'var(--c-bg)', borderRadius: 0 }}
          />
          <Legend wrapperStyle={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.10em' }} />
          <Line type="monotone" dataKey="feedbacks" stroke="var(--c-brand)" activeDot={{ r: 6 }} strokeWidth={2} name="Feedbacks" dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  // Phone calendar — 7-col, 40x40 cells, hairline borders, no chart.
  const renderPhoneCalendar = () => {
    const firstDay = new Date(selectedYear, selectedMonth, 1).getDay();
    const today = new Date();
    return (
      <div>
        <div className="db-cal-phone">
          {['SUN','MON','TUE','WED','THU','FRI','SAT'].map(d => (
            <div key={d} className="head">{d}</div>
          ))}
        </div>
        <div className="db-cal-phone" style={{ marginTop: 2 }}>
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} style={{ height: 40 }} />
          ))}
          {visualizationData.calendarData.map((dayData) => {
            const isToday = (
              dayData.date.getDate() === today.getDate() &&
              dayData.date.getMonth() === today.getMonth() &&
              dayData.date.getFullYear() === today.getFullYear()
            );
            const cls = ['cell',
              dayData.status === 'done' ? 'done' : '',
              dayData.status === 'not-done' ? 'nd' : '',
              dayData.status === 'week-off' ? 'wo' : '',
              isToday ? 'today' : '',
            ].filter(Boolean).join(' ');
            return (
              <div key={dayData.key} className={cls}>{dayData.day}</div>
            );
          })}
        </div>
      </div>
    );
  };

  // --- Phone mode: Departure Board phone variant -------------------------
  if (mode === 'phone') {
    return (
      <div className="db-page-phone">
        <PhoneTopStrip />

        <div className="db-ribbon-phone">
          <div className="title">
            SUPERVISOR · <em>{selectedSupervisor ? (selectedSupervisor.display_name || 'UNKNOWN').toUpperCase() : 'SELECT ONE'}</em>
          </div>
        </div>

        <div className="db-section-phone">
          <div className="db-form-row">
            <label>SELECT SUPERVISOR</label>
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.12em', color: 'var(--c-ink-3)' }}>
                <div className="spinner" /> LOADING…
              </div>
            ) : (
              <select
                value={selectedSupervisor ? selectedSupervisor.id : ''}
                onChange={(e) => {
                  const match = supervisors.find(s => s.id === e.target.value);
                  if (match) setSelectedSupervisor(match);
                }}
              >
                <option value="" disabled>SELECT SUPERVISOR</option>
                {supervisors.map((s) => (
                  <option key={s.id} value={s.id}>
                    {(s.display_name || 'UNKNOWN').toUpperCase()} · {s.ixora_user_id || 'NO ID'}{s.userStatus === 'inactive' ? ' · [INACTIVE]' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {selectedSupervisor && (
          <>
            <div className="db-section-phone" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 48, height: 48, flexShrink: 0, border: '1px solid var(--c-line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--c-bg-2)' }}>
                  {selectedSupervisor.photo_url ? (
                    <img src={selectedSupervisor.photo_url} alt="Supervisor" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.onerror = null; e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(selectedSupervisor.display_name || 'S'); }} />
                  ) : <PersonIcon style={{ fontSize: 28, color: 'var(--c-ink-3)' }} />}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 16, fontWeight: 600, color: 'var(--c-ink)', lineHeight: 1.2 }}>
                    {selectedSupervisor.display_name || 'Unnamed Supervisor'}
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--c-ink-3)', letterSpacing: '.10em', marginTop: 2 }}>
                    {selectedSupervisor.ixora_user_id || 'NO ID'}
                  </div>
                </div>
              </div>

              {[
                { label: 'IXORA ID', value: selectedSupervisor.ixora_user_id || '—' },
                { label: 'ALLOTMENT', value: supervisorZoneName(selectedSupervisor) || selectedSupervisor.ixora_allotment || '—' },
                { label: 'TOTAL FEEDBACKS', value: feedbackCount.toLocaleString('en-IN') },
                { label: 'PHONE', value: selectedSupervisor.phone_number || '—' },
                {
                  label: 'LAST CHECKLIST',
                  value: selectedSupervisor.lastChecklistDocDate
                    ? new Date(selectedSupervisor.lastChecklistDocDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '—',
                },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 0', borderBottom: '1px solid var(--c-line-soft)' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.14em', color: 'var(--c-ink-3)', textTransform: 'uppercase', fontWeight: 600 }}>{label}</span>
                  <span style={{ fontFamily: "'Geist', sans-serif", fontSize: 15, color: 'var(--c-ink)', fontWeight: 500 }}>{value}</span>
                </div>
              ))}

              {/* Employee management lives in IxoraOne. Deep-link when we know
                  the canonical uid; otherwise fall back to the employees list. */}
              <a
                href={selectedSupervisor?.ixoraone_uid
                  ? `https://one.ixoragroup.com/employees/${selectedSupervisor.ixoraone_uid}`
                  : 'https://one.ixoragroup.com/employees'}
                target="_blank"
                rel="noopener noreferrer"
                className="db-pill primary block"
                style={{ marginTop: 4, textDecoration: 'none', textAlign: 'center' }}
              >
                MANAGE IN IXORAONE ↗
              </a>
            </div>

            <div className="db-section-phone" style={{ borderTop: '1px solid var(--c-line-hard)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
                <div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.16em', color: 'var(--c-ink-3)', textTransform: 'uppercase', marginBottom: 2, fontWeight: 600 }}>CHECKLIST</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em', color: 'var(--c-ink)', fontWeight: 700 }}>
                    {months[selectedMonth].toUpperCase()} {selectedYear}
                  </div>
                </div>
                {renderMonthPicker()}
              </div>
              {renderPhoneCalendar()}
            </div>

            {/* Phone: Location Tracking */}
            <div className="db-section-phone" style={{ borderTop: '1px solid var(--c-line-hard)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.16em', color: 'var(--c-ink-3)', textTransform: 'uppercase', marginBottom: 2, fontWeight: 600 }}>LOCATION TRACKING</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em', color: 'var(--c-ink)', fontWeight: 700 }}>
                    {mapDate} · {mapStats.samples} SAMPLES
                  </div>
                </div>
                <input
                  type="date"
                  value={mapDate}
                  max={toLocalDateInputValue(new Date())}
                  onChange={(e) => setMapDate(e.target.value)}
                  style={{
                    fontFamily: 'var(--font-mono)', fontSize: 12,
                    fontVariantNumeric: 'tabular-nums slashed-zero',
                    color: 'var(--c-ink)', background: 'var(--c-bg)',
                    border: '1px solid var(--c-line-2)', padding: '6px 8px', borderRadius: 0,
                  }}
                />
              </div>
              {/* Phone stat strip */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, marginBottom: 10 }}>
                {[
                  { label: 'DISTANCE', value: `${mapStats.distanceKm.toFixed(2)} km` },
                  { label: 'DURATION', value: formatDuration(mapStats.durationMs) },
                  { label: 'SAMPLES', value: mapStats.samples },
                  { label: 'MOCK FLAGS', value: mapStats.mockedCount },
                ].map(({ label, value }) => (
                  <div key={label} style={{ border: '1px solid var(--c-line-soft)', padding: '8px 10px', background: 'var(--c-bg)' }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '.14em', color: 'var(--c-ink-3)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontVariantNumeric: 'tabular-nums slashed-zero', color: 'var(--c-ink)' }}>{value}</div>
                  </div>
                ))}
              </div>
              {/* Phone map */}
              <div style={{ height: 320, position: 'relative', background: 'var(--c-bg-2)', border: '1px solid var(--c-line-soft)' }}>
                <MapContainer
                  key="si-map-phone"
                  center={mapPoints[0] ? [mapPoints[0].lat, mapPoints[0].lng] : INDIA_CENTER}
                  zoom={mapPoints[0] ? 14 : 5}
                  style={{ width: '100%', height: '100%' }}
                  zoomControl
                >
                  <MapboxTileLayer />
                  <FitBounds points={mapPoints} />
                  {mapPoints.length >= 2 && (
                    <Polyline
                      positions={mapPoints.map((p) => [p.lat, p.lng])}
                      pathOptions={{ color: '#EA580C', opacity: 0.9, weight: 3 }}
                    />
                  )}
                  {mapPoints.length > 0 && (
                    <Marker position={[mapPoints[0].lat, mapPoints[0].lng]} icon={makeCircleIcon('#16a34a', 14)} />
                  )}
                  {mapPoints.length > 1 && (
                    <Marker position={[mapPoints[mapPoints.length - 1].lat, mapPoints[mapPoints.length - 1].lng]} icon={makeCircleIcon('#dc2626', 14)} />
                  )}
                </MapContainer>
                {locationLoading && (
                  <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 1000, background: 'var(--c-bg)', border: '1px solid var(--c-line-soft)', padding: '4px 8px', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', color: 'var(--c-ink-3)' }}>
                    LOADING…
                  </div>
                )}
                {!locationLoading && mapPoints.length === 0 && (
                  <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 1000, background: 'var(--c-bg)', border: '1px solid var(--c-line-soft)', padding: '4px 8px', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', color: 'var(--c-ink-3)' }}>
                    NO LOCATION SAMPLES FOR THIS DATE
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // --- Desk / wall: Departure Board ---
  return (
    <div className="db-page">
      <DepartureTopStrip />

      <div className="db-ribbon">
        <div className="db-ribbon-title">
          SUPERVISOR · <em>{selectedSupervisor ? (selectedSupervisor.display_name || 'UNKNOWN').toUpperCase() : 'SELECT ONE'}</em>
          {selectedSupervisor && (
            <span style={{ color: 'var(--c-ink-3)', fontWeight: 500, letterSpacing: '.10em', marginLeft: 12 }}>
              {selectedSupervisor.ixora_user_id || ''}
            </span>
          )}
        </div>
        <div className="db-ribbon-actions">
          <a
            href={selectedSupervisor?.ixoraone_uid
              ? `https://one.ixoragroup.com/employees/${selectedSupervisor.ixoraone_uid}`
              : 'https://one.ixoragroup.com/employees'}
            target="_blank"
            rel="noopener noreferrer"
            className="db-pill primary"
            style={{ textDecoration: 'none' }}
          >
            MANAGE IN IXORAONE ↗
          </a>
        </div>
      </div>

      <section style={{ padding: 'var(--sp-6) var(--pad-h) var(--sp-12)' }}>
        {/* Two-column layout: 320px selector+details | 1fr analytics */}
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 'var(--sp-8)', alignItems: 'start' }}>

          {/* Left: Selector + Details */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
            {/* Supervisor selector */}
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.16em', color: 'var(--c-ink-3)', marginBottom: 6, textTransform: 'uppercase' }}>Select Supervisor</div>
              {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.12em', color: 'var(--c-ink-3)' }}>
                  <div className="spinner" /> LOADING…
                </div>
              ) : renderSupervisorDropdown({ fontSize: 11 }, {})}
            </div>

            {/* Supervisor details */}
            {selectedSupervisor && (
              <div style={{ borderTop: '1px solid var(--c-line-hard)', paddingTop: 'var(--sp-5)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'var(--sp-3)' }}>
                  <div style={{ width: 48, height: 48, flexShrink: 0, border: '1px solid var(--c-line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--c-bg-2)' }}>
                    {selectedSupervisor.photo_url ? (
                      <img src={selectedSupervisor.photo_url} alt="Supervisor" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.onerror = null; e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(selectedSupervisor.display_name || 'S'); }} />
                    ) : <PersonIcon style={{ fontSize: 28, color: 'var(--c-ink-3)' }} />}
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 16, fontWeight: 600, color: 'var(--c-ink)', lineHeight: 1.2 }}>
                      {selectedSupervisor.display_name || 'Unnamed Supervisor'}
                    </div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--c-ink-3)', letterSpacing: '.10em', marginTop: 2 }}>
                      {selectedSupervisor.ixora_user_id || 'NO ID'}
                    </div>
                  </div>
                </div>

                {[
                  { label: 'AREA', value: supervisorZoneName(selectedSupervisor) || selectedSupervisor.ixora_allotment || '—' },
                  { label: 'PHONE', value: selectedSupervisor.phone_number || '—' },
                  { label: 'TOTAL FEEDBACKS', value: feedbackCount.toLocaleString('en-IN'), mono: true },
                  {
                    label: 'LAST CHECKLIST',
                    value: selectedSupervisor.lastChecklistDocDate
                      ? new Date(selectedSupervisor.lastChecklistDocDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                      : '—',
                    mono: true
                  },
                ].map(({ label, value, mono }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0', borderBottom: '1px solid var(--c-line-soft)', gap: 8, minWidth: 0 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.14em', color: 'var(--c-ink-3)', textTransform: 'uppercase', flexShrink: 0 }}>{label}</span>
                    <span style={{ fontFamily: mono ? 'var(--font-mono)' : "'Geist', sans-serif", fontSize: 13, color: 'var(--c-ink)', fontWeight: mono ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                      title={typeof value === 'string' ? value : undefined}
                    >{value}</span>
                  </div>
                ))}
                <a
                  href={selectedSupervisor?.ixoraone_uid
                    ? `https://one.ixoragroup.com/employees/${selectedSupervisor.ixoraone_uid}`
                    : 'https://one.ixoragroup.com/employees'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="db-pill primary block"
                  style={{ marginTop: 'var(--sp-4)', width: '100%', textDecoration: 'none', textAlign: 'center' }}
                >
                  MANAGE IN IXORAONE ↗
                </a>
              </div>
            )}
          </div>

          {/* Right: Analytics */}
          {selectedSupervisor ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-8)' }}>
              {/* Feedback line chart */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-4)' }}>
                  <div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.16em', color: 'var(--c-ink-3)', textTransform: 'uppercase', marginBottom: 2 }}>Daily Feedback Collection</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.10em', color: 'var(--c-ink-3)' }}>
                      {months[selectedMonth].toUpperCase()} {selectedYear} · {visualizationData.lineData.reduce((s, i) => s + i.feedbacks, 0)} FEEDBACKS
                    </div>
                  </div>
                  {renderMonthPicker()}
                </div>
                {/* Feedback stat strip */}
                {(() => {
                  const ld = visualizationData.lineData;
                  const total = ld.reduce((s, i) => s + i.feedbacks, 0);
                  const avg = ld.length > 0 ? (total / ld.length).toFixed(1) : '0.0';
                  const best = ld.length > 0 ? ld.reduce((a, b) => b.feedbacks > a.feedbacks ? b : a, ld[0]) : null;
                  const statCellStyle = { flex: 1, minWidth: 0, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 4 };
                  const eyebrowStyle = { fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--c-ink-3)', fontWeight: 500 };
                  const bigNumStyle = { fontFamily: 'var(--font-mono)', fontSize: 34, fontVariantNumeric: 'tabular-nums slashed-zero', color: 'var(--c-ink)', lineHeight: 1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
                  const metaStyle = { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--c-ink-3)', letterSpacing: '.10em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
                  return (
                    <div style={{ border: '1px solid var(--c-line-soft)', borderBottom: 'none', background: 'var(--c-bg)', display: 'flex', minWidth: 0 }}>
                      <div style={statCellStyle}>
                        <span style={eyebrowStyle}>TOTAL</span>
                        <span style={bigNumStyle}>{total}</span>
                        <span style={metaStyle}>FEEDBACKS THIS MONTH</span>
                      </div>
                      <div style={{ ...statCellStyle, borderLeft: '1px solid var(--c-line-soft)' }}>
                        <span style={eyebrowStyle}>AVG / DAY</span>
                        <span style={bigNumStyle}>{avg}</span>
                        <span style={metaStyle}>OVER {ld.length} DAYS</span>
                      </div>
                      <div style={{ ...statCellStyle, borderLeft: '1px solid var(--c-line-soft)' }}>
                        <span style={eyebrowStyle}>BEST DAY</span>
                        <span style={bigNumStyle}>{best ? best.feedbacks : '—'}</span>
                        <span style={metaStyle}>{best ? best.name : '—'}</span>
                      </div>
                    </div>
                  );
                })()}
                <div style={{ border: '1px solid var(--c-line-soft)', borderTop: '1px solid var(--c-line-soft)', padding: 'var(--sp-4)', background: 'var(--c-bg)' }}>
                  {renderLineChart()}
                </div>
              </div>

              {/* Checklist calendar */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-4)' }}>
                  <div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.16em', color: 'var(--c-ink-3)', textTransform: 'uppercase', marginBottom: 2 }}>Checklist Completion Status</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.10em', color: 'var(--c-ink-3)' }}>
                      {months[selectedMonth].toUpperCase()} {selectedYear} · {visualizationData.calendarData.filter(d => d.status === 'done' || d.status === 'week-off').length} ENTRIES
                    </div>
                  </div>
                </div>
                {/* Checklist stat strip */}
                {(() => {
                  const cd = visualizationData.calendarData;
                  const doneCount = cd.filter(d => d.status === 'done').length;
                  const notDoneCount = cd.filter(d => d.status === 'not-done').length;
                  const weekOffCount = cd.filter(d => d.status === 'week-off').length;
                  const statCellStyle = { flex: 1, minWidth: 0, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 4 };
                  const eyebrowStyle = { fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--c-ink-3)', fontWeight: 500 };
                  const metaStyle = { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--c-ink-3)', letterSpacing: '.10em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
                  const bigNumChecklist = { fontFamily: 'var(--font-mono)', fontSize: 34, fontVariantNumeric: 'tabular-nums slashed-zero', lineHeight: 1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
                  return (
                    <div style={{ border: '1px solid var(--c-line-soft)', borderBottom: 'none', background: 'var(--c-bg)', display: 'flex', minWidth: 0 }}>
                      <div style={statCellStyle}>
                        <span style={eyebrowStyle}>DONE</span>
                        <span style={{ ...bigNumChecklist, color: 'var(--c-success)' }}>{doneCount}</span>
                        <span style={metaStyle}>DAYS COMPLETED</span>
                      </div>
                      <div style={{ ...statCellStyle, borderLeft: '1px solid var(--c-line-soft)' }}>
                        <span style={eyebrowStyle}>NOT DONE</span>
                        <span style={{ ...bigNumChecklist, color: 'var(--c-danger)' }}>{notDoneCount}</span>
                        <span style={metaStyle}>DAYS MISSED</span>
                      </div>
                      <div style={{ ...statCellStyle, borderLeft: '1px solid var(--c-line-soft)' }}>
                        <span style={eyebrowStyle}>WEEK OFF</span>
                        <span style={{ ...bigNumChecklist, color: 'var(--c-ink-3)' }}>{weekOffCount}</span>
                        <span style={metaStyle}>DAYS OFF</span>
                      </div>
                    </div>
                  );
                })()}
                <div style={{ border: '1px solid var(--c-line-soft)', borderTop: '1px solid var(--c-line-soft)', padding: 'var(--sp-4)', background: 'var(--c-bg)' }}>
                  {renderCalendar()}
                </div>
              </div>

              {/* Location Tracking */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-4)' }}>
                  <div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.16em', color: 'var(--c-ink-3)', textTransform: 'uppercase', marginBottom: 2 }}>LOCATION TRACKING</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.10em', color: 'var(--c-ink-3)' }}>
                      {mapDate} · {mapStats.samples} SAMPLES
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => setShowFeedbackPins((v) => !v)}
                      style={{
                        fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em',
                        fontWeight: showFeedbackPins ? 700 : 400,
                        color: showFeedbackPins ? '#16a34a' : 'var(--c-ink-3)',
                        background: showFeedbackPins ? 'rgba(22,163,74,.08)' : 'transparent',
                        border: showFeedbackPins ? '1px solid #16a34a' : '1px solid var(--c-line-soft)',
                        padding: '4px 10px', cursor: 'pointer',
                      }}
                    >
                      {showFeedbackPins ? 'FEEDBACK ON' : 'SHOW FEEDBACK PINS'}
                    </button>
                    <input
                      type="date"
                      value={mapDate}
                      max={toLocalDateInputValue(new Date())}
                      onChange={(e) => setMapDate(e.target.value)}
                      style={{
                        fontFamily: 'var(--font-mono)', fontSize: 12,
                        fontVariantNumeric: 'tabular-nums slashed-zero',
                        color: 'var(--c-ink)', background: 'var(--c-bg)',
                        border: '1px solid var(--c-line-2)', padding: '6px 8px', borderRadius: 0,
                      }}
                    />
                  </div>
                </div>
                {/* Location stat strip */}
                {(() => {
                  const statCellStyle = { flex: 1, minWidth: 0, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 4 };
                  const eyebrowStyle = { fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--c-ink-3)', fontWeight: 500 };
                  const bigNumStyle = { fontFamily: 'var(--font-mono)', fontSize: 34, fontVariantNumeric: 'tabular-nums slashed-zero', color: 'var(--c-ink)', lineHeight: 1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
                  const metaStyle = { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--c-ink-3)', letterSpacing: '.10em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
                  return (
                    <div style={{ border: '1px solid var(--c-line-soft)', borderBottom: 'none', background: 'var(--c-bg)', display: 'flex', minWidth: 0 }}>
                      <div style={statCellStyle}>
                        <span style={eyebrowStyle}>DISTANCE</span>
                        <span style={bigNumStyle}>{mapStats.distanceKm.toFixed(2)}</span>
                        <span style={metaStyle}>KM</span>
                      </div>
                      <div style={{ ...statCellStyle, borderLeft: '1px solid var(--c-line-soft)' }}>
                        <span style={eyebrowStyle}>DURATION</span>
                        <span style={bigNumStyle}>{formatDuration(mapStats.durationMs)}</span>
                        <span style={metaStyle}>TRACKED</span>
                      </div>
                      <div style={{ ...statCellStyle, borderLeft: '1px solid var(--c-line-soft)' }}>
                        <span style={eyebrowStyle}>SAMPLES</span>
                        <span style={bigNumStyle}>{mapStats.samples}</span>
                        <span style={metaStyle}>GPS FIXES</span>
                      </div>
                      <div style={{ ...statCellStyle, borderLeft: '1px solid var(--c-line-soft)' }}>
                        <span style={eyebrowStyle}>MOCK FLAGS</span>
                        <span style={{ ...bigNumStyle, color: mapStats.mockedCount > 0 ? 'var(--c-danger)' : 'var(--c-ink)' }}>{mapStats.mockedCount}</span>
                        <span style={metaStyle}>SPOOFED</span>
                      </div>
                    </div>
                  );
                })()}
                {/* Map */}
                <div style={{ border: '1px solid var(--c-line-soft)', borderTop: '1px solid var(--c-line-soft)', background: 'var(--c-bg-2)', height: 420, position: 'relative' }}>
                  <MapContainer
                    key="si-map-desktop"
                    center={mapPoints[0] ? [mapPoints[0].lat, mapPoints[0].lng] : INDIA_CENTER}
                    zoom={mapPoints[0] ? 14 : 5}
                    style={{ width: '100%', height: '100%' }}
                    zoomControl
                  >
                    <MapboxTileLayer />
                    <FitBounds points={mapPoints} />
                    {mapPoints.length >= 2 && (
                      <Polyline
                        positions={mapPoints.map((p) => [p.lat, p.lng])}
                        pathOptions={{ color: '#EA580C', opacity: 0.9, weight: 3 }}
                      />
                    )}
                    {mapPoints.length > 0 && (
                      <Marker position={[mapPoints[0].lat, mapPoints[0].lng]} icon={makeCircleIcon('#16a34a', 14)} />
                    )}
                    {mapPoints.length > 1 && (
                      <Marker position={[mapPoints[mapPoints.length - 1].lat, mapPoints[mapPoints.length - 1].lng]} icon={makeCircleIcon('#dc2626', 14)} />
                    )}
                    {showFeedbackPins && feedbackPins.map((p) => (
                      <Marker
                        key={`fb-${p.id}`}
                        position={[p.lat, p.lng]}
                        icon={makeCircleIcon('#16a34a', 12)}
                        eventHandlers={{ click: () => navigate(`/feedback/admin/${p.id}`) }}
                        title={p.shopName}
                      />
                    ))}
                  </MapContainer>
                  {locationLoading && (
                    <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 1000, background: 'var(--c-bg)', border: '1px solid var(--c-line-soft)', padding: '6px 10px', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', color: 'var(--c-ink-3)' }}>
                      LOADING…
                    </div>
                  )}
                  {!locationLoading && mapPoints.length === 0 && (
                    <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 1000, background: 'var(--c-bg)', border: '1px solid var(--c-line-soft)', padding: '6px 10px', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', color: 'var(--c-ink-3)' }}>
                      NO LOCATION SAMPLES FOR THIS DATE
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--sp-12) 0', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', color: 'var(--c-ink-3)', textTransform: 'uppercase' }}>
              SELECT A SUPERVISOR TO VIEW ANALYTICS
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SupervisorInformation;
